import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import DateFilter from "../../../components/dateFilter";
import { primaryColor } from "../../../theme";
import { getRequestHeaders } from "../../utilities/auth/request";
import { formatErrorDate } from "../../utilities/date.util";
import { SortableTableCell } from "../SkillMappings/SkillDetails";
import { useDateFilter } from "../../../components/useDateFilter";
const UserActivityLog = () => {
  const [sortOrder, setSortOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("CREATED_DT");
  const [searchTerm, setSearchTerm] = useState("");
  const {
    selected,
    setSelected,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useDateFilter();
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const { isPending, isError, data, error } = useQuery({
    queryKey: ["user-activity", startDate, endDate],
    queryFn: async () => {
      const body = {
        fromDate: startDate,
        toDate: endDate,
      };
      const request = getRequestHeaders("post", body);
      const response = await fetch(`/ui/userActivity`, request);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      return data.flatMap(({ ACTIVITIES, ...rest }) =>
        ACTIVITIES.map((activity) => ({ ...rest, ...activity }))
      );
    },
  });

  if (isPending) {
    return <span>Loading...</span>;
  }

  if (isError) {
    return <span>Error: {error.message}</span>;
  }

  const handleSortClick = (property) => {
    const isAsc = orderBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  const sortedData = () =>
    data
      ? data
          .map((item) => {
            return { ...item, CREATED_DT: formatErrorDate(item.CREATED_DT) };
          })
          .sort((a, b) => {
            const order = sortOrder === "asc" ? 1 : -1;
            let aValue =
              orderBy === "CREATED_DT" ? new Date(a[orderBy]) : a[orderBy];
            let bValue =
              orderBy === "CREATED_DT" ? new Date(b[orderBy]) : b[orderBy];
            if (
              (aValue === null || aValue === undefined) &&
              (bValue === null || bValue === undefined)
            ) {
              return 1;
            }
            // Handle null or undefined values
            if (aValue === null || aValue === undefined) {
              return order;
            }
            if (bValue === null || bValue === undefined) {
              return -order;
            }

            // Normal string comparison
            if (aValue < bValue) {
              return -1 * order;
            }
            if (aValue > bValue) {
              return 1 * order;
            }
            return 0;
          })
      : [];

  const filteredData = () => {
    if (searchTerm) {
      return sortedData().filter((row) => {
        return Object.keys(row).some((key) =>
          row[key].toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    } else {
      return sortedData();
    }
  };
  const filteredDataList = filteredData();
  return (
    <>
      <Typography
        variant="h4"
        component={"div"}
        sx={{
          marginBottom: "1rem",
        }}
      >
        User Activity Log
      </Typography>
      <Box
        sx={{ backgroundColor: "white" }}
        width={"100%"}
        height={"calc(100vh - 128px)"}
        overflow={"auto"}
      >
        <Box display={"flex"} width={"60%"} justifyContent={"space-between"}>
          <Typography
            variant="h6"
            component={"div"}
            sx={{
              p: "1rem",
              fontWeight: "400",
            }}
          >
            <DateFilter
              selected={selected}
              setSelected={setSelected}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              setEndDate={setEndDate}
            />
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" component={"div"}>
              Filter Content
            </Typography>
            <TextField
              size="small"
              margin="normal"
              fullWidth
              sx={{
                padding: 0,
                margin: 0,
                paddingTop: "5px",
                minWidth: 200,
              }}
              id="search"
              name="search"
              autoComplete="off"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon style={{ color: primaryColor }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <TableContainer>
          <Box
            sx={{
              maxHeight: "calc(100vh - 230px)",
              overflow: "auto",
              ml: 2,
              mr: 2,
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <SortableTableCell
                    property="CREATED_DT"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "CREATED_DT"}
                  >
                    DATE & TIME
                  </SortableTableCell>
                  <SortableTableCell
                    property="USER_ID"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "USER_ID"}
                  >
                    SWA ID
                  </SortableTableCell>
                  <SortableTableCell
                    property="SKILL"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "SKILL"}
                  >
                    SKILL
                  </SortableTableCell>
                  <SortableTableCell
                    property="COURSE_CD"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "COURSE_CD"}
                  >
                    COURSE CODE
                  </SortableTableCell>
                  <SortableTableCell
                    property="COURSE_NAME"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "COURSE_NAME"}
                  >
                    COURSE NAME
                  </SortableTableCell>
                  <SortableTableCell
                    property="ACTION"
                    orderBy={orderBy}
                    sortOrder={sortOrder}
                    onClick={handleSortClick}
                    active={orderBy === "ACTION"}
                  >
                    ACTION
                  </SortableTableCell>
                </TableRow>
              </TableHead>
              <TableBody key={filteredDataList.length}>
                {filteredDataList?.map((row) => (
                  <TableRow>
                    <TableCell>{row.CREATED_DT}</TableCell>
                    <TableCell>{row.USER_ID}</TableCell>
                    <TableCell>{row.SKILL}</TableCell>
                    <TableCell>{row.COURSE_CD}</TableCell>
                    <TableCell>{row.COURSE_NAME}</TableCell>
                    <TableCell>{row.ACTION}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </TableContainer>
      </Box>
    </>
  );
};

export default UserActivityLog;
